import type { ReactNode } from 'react';
import { clsx } from 'clsx';

import { LineTabsBar } from './LineTabsBar';
import { useMasterDocumentTranslation } from './i18n';
import type { LineTabConfig, LineTabHelpers } from './MasterDocument.types';

export interface LineTabsRegionProps {
  /** Pestañas ya resueltas, en el orden en que se dibujan. */
  tabs: LineTabConfig[];
  /** Pestaña activa. Si la clave no existe, cae a la primera. */
  activeKey: string;
  onChange: (key: string) => void;
  /**
   * Contenido de la pestaña Tipo 1. Recibe `hideTitle` porque el título de la
   * grilla solo estorba cuando hay una barra que ya nombra la pestaña.
   */
  renderLines: (opts: { hideTitle: boolean }) => ReactNode;
  /**
   * Ayudantes del nivel presentacional para los contenidos Tipo 2 / Tipo 3.
   * Los aporta `MasterDocument`, que es quien tiene el estado del modal de línea.
   */
  helpers: LineTabHelpers;
  className?: string;
}

/**
 * Región de detalle con pestañas: dibuja la barra y monta el contenido de la
 * pestaña activa.
 *
 * Dos decisiones que parecen detalles y no lo son:
 *
 * 1. **Una sola pestaña no dibuja barra.** Una pestaña sola no es navegación, y
 *    el documento se ve idéntico a como se veía sin la capacidad.
 * 2. **Al cambiar de pestaña el contenido se DESMONTA**, no se oculta con
 *    `display:none`. La grilla nativa virtualiza midiendo su contenedor de
 *    scroll (`useVirtualizer`): oculta por CSS mide cero y al volver pinta cero
 *    filas o alto cero. El precio es perder el scroll de la pestaña saliente; el
 *    host que quiera conservarlo cachea el estado en su renderer.
 *
 * Renderiza un fragmento —no un contenedor propio— para que la barra y el panel
 * sigan siendo hijos directos de la columna del documento y conserven su
 * separación. El panel sí es un contenedor con la misma separación interna,
 * porque la pestaña de líneas apila varios bloques (menú de clases, aviso de
 * datos obsoletos, grilla).
 *
 * @see LineTabsBar — la barra
 * @see MasterDocument — decide qué pestañas existen y compone la de líneas
 */
export function LineTabsRegion({
  tabs,
  activeKey,
  onChange,
  renderLines,
  helpers,
  className,
}: LineTabsRegionProps) {
  const { t } = useMasterDocumentTranslation();

  if (tabs.length === 0) return null;

  const showBar = tabs.length > 1;
  const activeTab = tabs.find((tab) => tab.key === activeKey) ?? tabs[0];

  const content =
    activeTab.type === 'lines' ? (
      renderLines({ hideTitle: showBar })
    ) : activeTab.render ? (
      activeTab.render(helpers)
    ) : (
      // El template declaró un `renderKey` que el host no registró. Se avisa en
      // consola desde el contenedor smart, que es quien conoce el registro; aquí
      // solo queda dejar la pestaña con un estado explícito en vez de vacía.
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-sm font-medium text-content-secondary dark:text-dark-content-secondary">
          {t('master_document.lines.tabUnavailable')}
        </p>
      </div>
    );

  return (
    <>
      {showBar && <LineTabsBar tabs={tabs} activeKey={activeTab.key} onChange={onChange} />}
      <div
        role="tabpanel"
        id={`line-tabpanel-${activeTab.key}`}
        aria-labelledby={showBar ? `line-tab-${activeTab.key}` : undefined}
        className={clsx('flex flex-col gap-[10px] min-w-0', className)}
      >
        {content}
      </div>
    </>
  );
}
